import React, { useState } from 'react';

import { CTAConfig } from '@oneaudi/vtp-configuration-service';
import {
  getLeanCheckoutBackendForButton,
  useServicesContext,
  VehicleBasic,
  openFocusLayerV2,
  mapButtonTypeToIconName,
  trackClick,
  trackGroupLeanCheckoutClick,
} from '@oneaudi/vtp-shared';
import { Icon, IconButton, TextButton } from '@oneaudi/unified-web-components';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { TilesRow, TilesRowItem, IconWrapper } from '../styles/ConversionBar.styles';
import { ContentService } from '@oneaudi/content-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { AllCheckoutType, EcommerceServiceV1 } from '@volkswagen-onehub/e-commerce-service';
import type { Logger } from '@feature-hub/logger';
import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';

interface RenderedButtonProps {
  vehicle: VehicleBasic;
  scContextId: string;
  buttonsState: CTAConfig[];
  isColumn: boolean;
  onClickToOpenFSPopup?: (params: {
    buttonInfo: any;
    trackButtonClick: any;
    scContextId?: string;
    isMobileScreenSize: boolean;
    isLoading?: boolean;
    isRenderedInConversionBar?: boolean;
    IsLayerOpenedFromConversionBar?: boolean;
  }) => void;
}
enum DeviceType {
  Mobile = 'Mobile',
  Tablet = 'Tablet',
  Desktop = 'Desktop',
}

export const RenderedButton: React.FC<RenderedButtonProps> = ({
  vehicle,
  scContextId,
  buttonsState,
  isColumn,
  onClickToOpenFSPopup,
}) => {
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const eCommerceService: EcommerceServiceV1 = useServicesContext().getAdditionalService(
    'e-commerce-service'
  ) as EcommerceServiceV1;
  const navigationService: NavigationServiceV1 = useServicesContext().getAdditionalService(
    'navigation-service'
  ) as NavigationServiceV1;
  const logger = (useServicesContext().getAdditionalService('s2:logger') ?? console) as Logger;
  const [deviceType, setDeviceType] = React.useState<DeviceType>(DeviceType.Mobile);
  const checkDeviceType = () => {
    if (window.matchMedia('(max-width: 768px)').matches) setDeviceType(DeviceType.Mobile);
    else if (window.matchMedia('(max-width: 1023px)').matches) setDeviceType(DeviceType.Tablet);
    else setDeviceType(DeviceType.Desktop);
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      checkDeviceType();
      window.addEventListener('resize', checkDeviceType);
      return () => {
        window.removeEventListener('resize', checkDeviceType);
      };
    }
    return undefined;
  }, []);

  const trackButtonClick = (
    buttoninfo: CTAConfig,
    variant: string,
    trackingCode?: string,
    btnName?: string
  ) => {
    trackClick(
      trackingService,
      buttoninfo,
      variant,
      'vtp-carInfo',
      vehicle.id,
      trackingCode,
      btnName
    );
  };

  const renderByDealerId = (button: any): boolean => {
    const filterType = button.options_filterByDealerId_filterType;
    const filterList = button.options_filterByDealerId_filterList;

    if (filterType && filterList) {
      const dealerArray = filterList.split(',').map((item: string) => item.trim());
      const dealerIdInArray = dealerArray.find(
        (dealerId: string) => dealerId === vehicle.dealer.id
      );

      if (filterType === 'include') {
        return !!dealerIdInArray;
      }
      if (filterType === 'exclude') {
        return !dealerIdInArray;
      }
    }

    if (filterType === 'include' && !filterList) {
      return false;
    }

    return true;
  };

  const filteredButtons = buttonsState.filter(renderByDealerId);

  return (
    <TilesRow isColumn={isColumn}>
      {filteredButtons.map((button: CTAConfig, index) => {
        const iconName = mapButtonTypeToIconName(button.type, button.iconName);
        const variant = index > 0 ? 'secondary' : 'primary';

        // group lean checkout button?
        const checkoutBackend = getLeanCheckoutBackendForButton(button, eCommerceService, vehicle);
        if (checkoutBackend) {
          const leanCheckoutClick = (checkout: AllCheckoutType) => {
            try {
              trackGroupLeanCheckoutClick(
                trackingService,
                navigationService,
                button,
                variant,
                'vtp-carInfo',
                vehicle,
                logger
              );
              eCommerceService.startCheckout(checkout);
            } catch (e) {
              logger.error(JSON.stringify(e));
            }
          };

          return (
            <React.Fragment key={button.label}>
              {deviceType === DeviceType.Desktop || index === 0 || !isColumn ? (
                <TilesRowItem key={button.label}>
                  <TextButton
                    aria-label={button.label}
                    size="sm"
                    data-testid={`${button.type}-button-variant-${variant}`}
                    variant={!isColumn ? 'tertiary' : variant}
                    onClick={() => {
                      leanCheckoutClick(checkoutBackend);
                    }}
                  >
                    {!isColumn && <Icon name={iconName || 'forward-active'} size="s" />}
                    {button.label}
                  </TextButton>
                </TilesRowItem>
              ) : (
                <IconWrapper>
                  <IconButton
                    icon={iconName ?? 'forward-active'}
                    data-testid={`${button.type}-button-variant-${variant}`}
                    size="sm"
                    title={button.label}
                    variant="secondary"
                    onClick={() => {
                      leanCheckoutClick(checkoutBackend);
                    }}
                  />
                </IconWrapper>
              )}
            </React.Fragment>
          );
        }

        if (button.method === 'POST') {
          const [postData, setPostData] = useState('');
          const postDataString = JSON.stringify([
            { audicode: vehicle.audiCode },
            { financingData: vehicle.financing },
            { vehicleData: vehicle },
          ]);

          if (postData !== postDataString) {
            setPostData(postDataString);
          }

          if (button.target === 'open-in-layer') {
            const layerManager = useServicesContext().getAdditionalService(
              'gfa:layer-manager'
            ) as GfaLayerManagerV1;

            const servicesContext = useServicesContext();
            const audiContentService = servicesContext.getAdditionalService(
              'audi-content-service'
            ) as ContentService;

            const iframeForms =
              audiContentService.getContent()?.fields?.vtpConfiguration?.fields
                ?.scopes_iframeForms || false;

            return (
              <React.Fragment key={button.label}>
                {deviceType === DeviceType.Desktop || index === 0 || !isColumn ? (
                  <TilesRowItem key={button.label}>
                    <TextButton
                      size="sm"
                      data-testid={`${button.type}-button-variant-${variant}`}
                      variant={!isColumn ? 'tertiary' : variant}
                      onClick={() => {
                        trackButtonClick(button, variant);
                        openFocusLayerV2(button as any, postData, layerManager, iframeForms);
                      }}
                    >
                      {!isColumn && <Icon name={iconName || 'forward-active'} size="s" />}
                      {button.label}
                    </TextButton>
                  </TilesRowItem>
                ) : (
                  <IconWrapper>
                    <IconButton
                      icon={iconName ?? 'forward-active'}
                      data-testid={`${button.type}-button-variant-${variant}`}
                      size="sm"
                      title={button.label}
                      variant="secondary"
                      onClick={() => {
                        trackButtonClick(button, variant);
                        openFocusLayerV2(button as any, postData, layerManager, iframeForms);
                      }}
                    />
                  </IconWrapper>
                )}
              </React.Fragment>
            );
          }

          // Finance Checkout button
          if (vehicle.financing && button.type === 'finance-checkout') {
            return (
              <React.Fragment key={button.label}>
                {deviceType === DeviceType.Desktop || index === 0 || !isColumn ? (
                  <TilesRowItem key={button.label}>
                    <TextButton
                      size="sm"
                      data-testid={`${button.type}-button-variant-${variant}`}
                      variant={!isColumn ? 'tertiary' : variant}
                      onClick={() => {
                        trackButtonClick(button, variant);
                        if (button.type === 'finance-checkout' && onClickToOpenFSPopup) {
                          onClickToOpenFSPopup({
                            buttonInfo: button,
                            trackButtonClick,
                            scContextId,
                            isMobileScreenSize: deviceType !== DeviceType.Desktop,
                            isLoading: false,
                            isRenderedInConversionBar: true,
                            IsLayerOpenedFromConversionBar: true,
                          });
                        }
                      }}
                    >
                      {!isColumn && <Icon name={iconName || 'forward-active'} size="s" />}
                      {button.label}
                    </TextButton>
                  </TilesRowItem>
                ) : (
                  <IconWrapper>
                    <IconButton
                      icon={iconName ?? 'forward-active'}
                      data-testid={`${button.type}-button-variant-${variant}`}
                      size="sm"
                      title={button.label}
                      variant="secondary"
                      onClick={() => {
                        trackButtonClick(button, variant);
                        if (button.type === 'finance-checkout' && onClickToOpenFSPopup) {
                          onClickToOpenFSPopup({
                            buttonInfo: button,
                            trackButtonClick,
                            scContextId,
                            isMobileScreenSize: false,
                            isLoading: false,
                            isRenderedInConversionBar: true,
                            IsLayerOpenedFromConversionBar: true,
                          });
                        }
                      }}
                    />
                  </IconWrapper>
                )}
              </React.Fragment>
            );
          }

          // Post button
          return (
            <React.Fragment key={button.label}>
              {deviceType === DeviceType.Desktop || index === 0 || !isColumn ? (
                <TilesRowItem key={button.label}>
                  <form
                    method="post"
                    data-testid="post-button-form"
                    action={button.url}
                    target={button.target === 'new-window' ? '_blank' : '_self'}
                  >
                    <TextButton
                      aria-label="Button Label"
                      iconPosition="leading"
                      onClick={() => trackButtonClick(button, variant)}
                      type="submit"
                      size="sm"
                      variant={!isColumn ? 'tertiary' : variant}
                      data-testid={`${button.type}-button-variant-${variant}`}
                    >
                      {!isColumn && <Icon name={iconName || 'forward-active'} size="s" />}
                      {button.label}
                    </TextButton>
                    <input type="hidden" name="inquiry_json_data" value={postData} />
                  </form>
                </TilesRowItem>
              ) : (
                <IconWrapper>
                  <IconButton
                    type="submit"
                    icon={iconName ?? 'forward-active'}
                    size="sm"
                    onClick={() => trackButtonClick(button, variant)}
                    title={button.label}
                    variant="secondary"
                    data-testid={`${button.type}-button-variant-${variant}`}
                  />
                </IconWrapper>
              )}
            </React.Fragment>
          );
        }

        // GET | Phone Button button
        return (
          <React.Fragment key={button.label}>
            {deviceType === DeviceType.Desktop || index === 0 || !isColumn ? (
              <TilesRowItem key={button.label}>
                <TextButton
                  aria-label="Button Label"
                  iconPosition="leading"
                  href={
                    button.type === 'phone' ? `tel:${button?.url?.replace(/\s/g, '')}` : button.url
                  }
                  onClick={() => trackButtonClick(button, variant)}
                  target={button.target === 'new-window' ? '_blank' : '_self'}
                  rel="noopener"
                  size="sm"
                  variant={!isColumn ? 'tertiary' : variant}
                  data-testid={`${button.type}-button-variant-${variant}`}
                  isFullWidth={deviceType === DeviceType.Mobile}
                >
                  {!isColumn && <Icon name={iconName ?? 'forward-active'} size="s" />}
                  {button.label}
                </TextButton>
              </TilesRowItem>
            ) : (
              <IconWrapper>
                <IconButton
                  href={
                    button.type === 'phone' ? `tel:${button?.url?.replace(/\s/g, '')}` : button.url
                  }
                  icon={iconName ?? 'forward-active'}
                  rel="noopener"
                  size="sm"
                  target={button.target === 'new-window' ? '_blank' : '_self'}
                  title={button.label}
                  variant="secondary"
                  data-testid={`${button.type}-button-variant-${variant}`}
                  onClick={() => trackButtonClick(button, variant)}
                />
              </IconWrapper>
            )}
          </React.Fragment>
        );
      })}
    </TilesRow>
  );
};
